import React, { useCallback, useEffect, useState } from 'react';

import Guidance from '../common/Guidance';
import CopyReferencesModal from './CopyReferencesModal';
import { canManage } from '../../utils/permissions';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { getGlobalSources } from '../../services/publication';

/**
 * RETIRING SOURCE (docs/plans/personal-discover-global.md §4.6): when a
 * global collection this collection's cards look up into is retiring, its
 * home says so to the people who can act — managers — naming the cards
 * affected and, on the copy path, offering **Copy referenced records…**.
 * On the archive path the records move to the keepr archive by themselves,
 * so it says nothing is needed. Readers never see it.
 */
export default function RetiringSources({ collection, collectionId }) {
	const [sources, setSources] = useState([]);
	const [copying, setCopying] = useState(null);
	const manager = Boolean(collection) && canManage(collection);

	const load = useCallback(() => {
		if (!manager || !collectionId) { setSources([]); return; }
		getGlobalSources(collectionId)
			.then(rows => setSources(rows.filter(row => row.state === 'retiring')))
			.catch(() => setSources([]));
	}, [manager, collectionId]);
	useEffect(() => { load(); }, [load]);

	if (!sources.length) return null;
	return (
		<>
			{sources.map(source => {
				const date = source.retireAt ? formatDisplayDate(source.retireAt) : 'soon';
				const cards = source.cards.map(c => c.name).join(', ');
				const archive = source.retirePath === 'archive';
				return (
					<Guidance
						key={source.collectionId}
						tone={archive ? 'info' : 'caution'}
						className="retiring-source"
						title={`${source.name} is retiring on ${date}`}
						why={archive
							? `${cards} ${source.cards.length === 1 ? 'links' : 'link'} to it. The records you link to move to the keepr archive on that date, where every account can still read them — nothing is needed from you.`
							: `${cards} ${source.cards.length === 1 ? 'links' : 'link'} to it — ${source.items === 1 ? '1 record here holds' : `${source.items} records here hold`} a link. After that date those links stop resolving. Copy what you link to, to keep it.`}
						action={archive ? null : { label: 'Copy referenced records…', onClick: () => setCopying(source) }}
						links={['items/copy-a-record-from-a-retiring-collection']}
					/>
				);
			})}
			{copying ? (
				<CopyReferencesModal
					open
					onClose={() => setCopying(null)}
					collectionId={collectionId}
					source={{ collectionId: copying.collectionId, name: copying.name }}
					onDone={() => { setCopying(null); load(); }}
				/>
			) : null}
		</>
	);
}
